import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { PlusIcon, CheckIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
const API_BASE =
  (process.env.REACT_APP_API_URL &&
    process.env.REACT_APP_API_URL.replace(/\/+$/, '')) ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : window.location.origin);
const STATUS = {
  PENDING: 'PENDIENTE',
  FULFILLED: 'SURTIDO',
  UNFULFILLED: 'NO SURTIDO',
};

function TransfersModule() {
  const { token, user } = useAuth();
  const [skuInput, setSkuInput] = useState('');
  const [transferList, setTransferList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Carga la sesión guardada del backend
  const fetchSession = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/transfer-sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Asegúrate de que los ítems tengan un 'id' para evitar errores de clave en React
        const loadedList = (data.transfer_data || []).map(item => ({
          ...item,
          id: item.id || Date.now() + Math.random() // Añade un ID si no existe (para ítems antiguos)
        }));
        setTransferList(loadedList);
      } else {
        const error = await response.json();
        setMessage(`Error al cargar la sesión: ${error.mensaje}`);
      }
    } catch (err) {
      console.error('Error cargando sesión:', err);
      setMessage('Error de conexión al cargar la sesión de traspaso.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Guarda la sesión en el backend cada vez que la lista cambia
  const saveSession = useCallback(async (listToSave) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/transfer-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ data: listToSave }),
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to save session:', error);
      }
    } catch (err) {
      console.error('Error saving session:', err);
    } finally {
      setIsSaving(false);
    }
  }, [token]);

  const fetchSkuDetails = useCallback(async (sku) => {
    setMessage('');
    try {
      const response = await fetch(`/api/inventory?searchTerm=${sku}&limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.inventory.length > 0) {
        return data.inventory[0];
      }
      return null;
    } catch (err) {
      console.error('Error al buscar SKU:', err);
      setMessage('Error de conexión al buscar el SKU.');
      return null;
    }
  }, [token]);

  const handleAddSku = async (e) => {
    e.preventDefault();
    if (!skuInput.trim()) return;

    setLoading(true);
    const sku = skuInput.trim();

    if (transferList.find(item => item.sku === sku)) {
      setMessage('El SKU ya está en la lista.');
      setLoading(false);
      return;
    }

    const skuDetails = await fetchSkuDetails(sku);
    if (skuDetails) {
      const newList = [...transferList, {
        sku: skuDetails.internal_sku,
        description: skuDetails.internal_title || 'N/A',
        quantity: 1,
        status: STATUS.PENDING,
        id: Date.now(), // ID único para React, se puede generar de forma más robusta si es necesario
        addedAt: new Date().toISOString() // <-- AÑADIDO: Fecha en que el SKU se añade a la lista
      }];
      setTransferList(newList);
      saveSession(newList); // Guarda la sesión al añadir
      setMessage('');
      setSkuInput('');
    } else {
      setMessage('SKU no encontrado en el inventario.');
    }
    setLoading(false);
  };

  const handleStatusChange = (id, newStatus) => {
    const newList = transferList.map(item => item.id === id ? { ...item, status: newStatus } : item);
    setTransferList(newList);
    saveSession(newList); // Guarda la sesión al cambiar estatus
  };

  const handleQuantityChange = (id, newQuantity) => {
    const newList = transferList.map(item => item.id === id ? { ...item, quantity: parseInt(newQuantity) } : item);
    setTransferList(newList);
    saveSession(newList); // Guarda la sesión al cambiar cantidad
  };

  const handleRemoveItem = (id) => {
    const newList = transferList.filter(item => item.id !== id);
    setTransferList(newList);
    saveSession(newList); // Guarda la sesión al eliminar
    setMessage('Ítem eliminado de la lista.');
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Módulo de Surtido</h2>
        {isSaving && (
          <span className="text-sm text-gray-500 animate-pulse">Guardando...</span>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Añadir SKUs al surtido</h3>
        <form onSubmit={handleAddSku} className="flex items-center space-x-4">
          <input
            type="text"
            className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline flex-grow"
            placeholder="Introduce SKU"
            value={skuInput}
            onChange={(e) => setSkuInput(e.target.value)}
            disabled={loading}
          />
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center transition duration-200 disabled:opacity-50"
            disabled={loading || isSaving}
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Añadir
          </button>
        </form>
        {message && (
          <div className={`mt-4 p-2 rounded-lg text-sm ${message.includes('Error') || message.includes('no encontrado') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {message}
          </div>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Surtido CEDIS&gt;LAFE ({transferList.length})</h3>
        <div className="overflow-x-auto">
          {loading ? (
            <p className="text-gray-500 text-center">Cargando...</p>
          ) : transferList.length === 0 ? (
            <p className="text-gray-500 text-center">Añade SKUs para empezar el surtido.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estatus</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Añadido</th> {/* <-- NUEVA COLUMNA */}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transferList.map(item => (
                  <tr key={item.id} className={item.status === STATUS.FULFILLED ? 'bg-green-50' : item.status === STATUS.UNFULFILLED ? 'bg-red-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.sku}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                        className="w-16 text-center border-gray-300 rounded-md shadow-sm"
                        min="1"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        item.status === STATUS.FULFILLED ? 'bg-green-100 text-green-800' :
                        item.status === STATUS.UNFULFILLED ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"> {/* <-- CELDA DE FECHA */}
                      {item.addedAt ? new Date(item.addedAt).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {item.status !== STATUS.FULFILLED && (
                          <button
                            onClick={() => handleStatusChange(item.id, STATUS.FULFILLED)}
                            className="text-green-600 hover:text-green-900"
                            title="Marcar como Surtido"
                          >
                            <CheckIcon className="h-5 w-5" />
                          </button>
                        )}
                        {item.status !== STATUS.UNFULFILLED && (
                          <button
                            onClick={() => handleStatusChange(item.id, STATUS.UNFULFILLED)}
                            className="text-red-600 hover:text-red-900"
                            title="Marcar como No Surtido"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Eliminar de la lista"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

export default TransfersModule;